import {
  Breadcrumbs,
  Inline,
  Stack,
  Typography,
} from '@omnipresentgroup/design-system';
import { useBenefits } from '@omnipresentgroup/domains';
import {
  BENEFIT_TYPE_PARAM,
  BenefitFilters,
  BenefitList,
} from '@omnipresentgroup/features';
import {
  COUNTRY_CODE_PARAM,
  EMPLOYEE_ID_PARAM,
} from '@omnipresentgroup/shared';
import type { Benefits, BenefitType } from '@omnipresentgroup/types';
import { useNavigate, useSearchParams } from 'react-router';

import { useManagerUser } from '../../hooks';

const PAGE_TITLE = 'Benefits';
const DEFAULT_BENEFITS: Benefits = {
  benefits: [],
  lastUpdated: '',
};

export const BenefitsPage = () => {
  const navigate = useNavigate();

  const { companyId } = useManagerUser();

  const [searchParameters] = useSearchParams();

  const employeeId = new URLSearchParams(searchParameters).get(
    EMPLOYEE_ID_PARAM,
  ) as string;

  const countryCode = new URLSearchParams(searchParameters).get(
    COUNTRY_CODE_PARAM,
  ) as string;

  const benefitType = new URLSearchParams(searchParameters).get(
    BENEFIT_TYPE_PARAM,
  ) as string;

  const {
    data: benefitsInfo = DEFAULT_BENEFITS,
    isLoading,
    isError,
  } = useBenefits({
    employeeId: employeeId,
    countryCode: countryCode,
    benefitType: benefitType as BenefitType,
  });

  const PageHeader = () => {
    return (
      <Inline justify="space-between" minH={40} align="center">
        <Typography as="h2" hideParagraphSpacing size="24" weight="medium">
          {PAGE_TITLE}
        </Typography>
      </Inline>
    );
  };

  return (
    <>
      <Stack gap="16">
        <Breadcrumbs
          crumbs={[
            { id: 'home', title: 'Home', onClick: () => navigate('/') },
            {
              id: 'benefits',
              title: PAGE_TITLE,
              active: true,
            },
          ]}
        />
        <PageHeader />
      </Stack>

      <BenefitList
        benefitsInfo={benefitsInfo}
        isLoading={isLoading}
        isError={isError}
        columnVisibility={{
          employeeName: true,
          benefit: true,
          effectiveDate: true,
          details: true,
          employerCost: true,
          country: true,
        }}
        showHeader={true}
        filterSection={
          <BenefitFilters
            benefits={benefitsInfo?.benefits}
            companyId={companyId}
            enable={{ employee: true, country: true, benefitType: true }}
          />
        }
        emptyStateProps={{
          id: 'benefits-empty-state',
          title: 'No benefits found',
        }}
      />
    </>
  );
};
